import React, { useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
    Button,
    Dialog,
    Modal,
    Tabs,
    Tab,
    Box,
    Stack,
    Divider,
    Grid,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import ClearAlignerThumbnails from './ClearAlignerThumbnails';
import NonClearAlignerPdf from './NonClearAlignerPdf';
import Renders from './Renders';
import Download from '../Download';
import { LabPicker } from '../..';
import AnimateOnMount from '../../AnimateOnMount';
import './styles.scss';
import { getCaseDownloadPermissions, hasAccess, http } from '../../../helpers';
import { Ori } from '../../../assets/theme/js/variables';

const caseDetailsTheme = createTheme({
    palette: {
        primary: {
            main: Ori.darkerGray,
        },
        secondary: {
            main: Ori.white,
        },
    },
    typography: {
        fontFamily: 'inherit',
        fontColor: Ori.darkerGray,
    },
});

function CaseDetail({
    id,
    orderIntegrationUploadZipUrl,
    prescriptionUrl,
    scanZipUrl,
    thumbnailUrl,
    template,
    // instructions,
    patientName,
    doctorName,
    labId,
    labName,
    clinicName,
    status,
    createdOn,
    receivedOn,
    dueOn,
    scans,
    numCases,
    index,
    labs,
    setOpen,
    caseDetailGlobalState,
    setCaseDetailGlobalState,
    onCaseStatusChange,
    onCaseLabChange,
}) {
    const [showFullID, setShowFullID] = useState(false);
    const [showAddLabModal, setShowAddLabModal] = useState(false);
    const [chosenLab, setChosenLab] = useState({});
    const formatDate = (d) => (d ? moment(d).format('D MMM YYYY') : '-');

    const [showOverlay, setShowOverlay] = useState(false);
    const timeoutRef = useRef(null);
    const handleOverlay = () => {
        setShowOverlay(true);

        clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            setShowOverlay(false);
        }, 1000);
    };

    const shortenId = (x) => x.substring(0, 8);
    const id8 = shortenId(id);
    const displayFullId = id != null ? id.replaceAll('-', '\u2011') : '-';
    const getIDString = (type) => (type === 'short' ? id8 : id);
    const onCopy = (type) => {
        navigator.clipboard.writeText(getIDString(type));

        if (type === 'short') setShowFullID(false);
        else setShowFullID(true);

        handleOverlay();
    };

    const closeViewer = () => setOpen(null);
    const prevCase = () => setOpen(index - 1);
    const nextCase = () => setOpen(index + 1);
    const [leftPanel, setLeftPanel] = useState(0); // default to PDF viewer
    const {
        isRightPanelOpen,
        rightPanel,
        showRenderTips,
    } = caseDetailGlobalState;
    const canShowActionsTab = status !== 'Processing' && status !== 'Processing Failed';
    const selectedRightPanel = canShowActionsTab ? rightPanel : 1;
    const handleRightPanelChange = (event, newValue) => {
        setCaseDetailGlobalState((currentState) => ({
            ...currentState,
            rightPanel: newValue,
        }));
    };
    const handleLeftPanelChange = (newValue) => {
        setLeftPanel(newValue);
    };

    const {
        canDownloadOrderIntegrationUploadZip,
        canDownloadPrescription,
        canUpdateCaseStatus,
        canDownloadSTL,
    } = getCaseDownloadPermissions();

    const orderedScans = useMemo(
        () => [...scans].sort((a, b) => a.displayIndex - b.displayIndex),
        [scans],
    );

    const [activeViewIndex, setActiveViewIndex] = useState(0);

    const [viewMode, setViewMode] = useState('thumbnail');
    const activeScan = orderedScans[activeViewIndex];
    const activeUrl = useMemo(() => {
        if (!activeScan) return null;

        switch (viewMode) {
            case 'stl':
                return activeScan.stlUrl;
            case 'ply':
                return activeScan.plyUrl;
            default:
                return activeScan.thumbnailUrl;
        }
    }, [activeScan, viewMode]);

    const onCloseAddLabModal = () => setShowAddLabModal(false);
    const onOpenAddLabModal = () => setShowAddLabModal(true);
    const onMoveCaseToLab = () => {
        const payload = { id, labId: chosenLab?.id || 'none', labName: chosenLab?.name };
        http.put(`/case-api/cases/${id}`, payload)
            .then((res) => {
                if (res.status === 200) {
                    onCaseLabChange(id, payload.labId, payload.labName);
                }
            })
            .catch((error) => {
                console.warn(error);
            });
        onCloseAddLabModal();
    };

    useEffect(() => {
        if (!activeScan) return;

        if (viewMode === 'stl' && !activeScan.stlUrl) {
            setViewMode('thumbnail');
        }

        if (viewMode === 'ply' && !activeScan.plyUrl) {
            setViewMode('thumbnail');
        }
    }, [activeScan, viewMode]);

    return (
        <ThemeProvider theme={caseDetailsTheme}>
            <Modal open onClose={closeViewer} hideBackdrop>
                <Box className="fullscreen-viewer">
                    <Box className="top-bar" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Box className="exit-viewer">
                            <i className="material-icons" role="presentation" onClick={closeViewer}>close</i>
                        </Box>
                        <Stack direction="row" className="case-information" divider={<Divider orientation="vertical" flexItem sx={{ my: '5px' }} />}>
                            <Box className="heading no-caps">
                                Case:
                                {' '}
                                {id8}
                            </Box>
                            <Box className="info">
                                {
                                    status === 'Processing Failed'
                                        ? (
                                            <i className="material-icons status failed">warning</i>
                                        )
                                        : (
                                            status === 'Processing'
                                                ? (
                                                    <i className="material-icons status pending">timelapse</i>
                                                )
                                                : (
                                                    status === 'Submitted'
                                                        ? (
                                                            <i className="material-icons status processing">timelapse</i>
                                                        )
                                                        : (
                                                            <i className="material-icons status completed">check_circle_outline</i>
                                                        )
                                                )
                                        )
                                }
                                {status != null ? status : '-'}
                            </Box>
                            <Box className="info">
                                <HomeWorkOutlinedIcon />
                                {clinicName !== null ? clinicName : '-'}
                            </Box>
                            <Box className="info">
                                <i className="material-icons" role="presentation">calendar_month</i>
                                Created:
                                {' '}
                                {formatDate(createdOn)}
                            </Box>
                            <Box className="info">
                                <i className="material-icons" role="presentation">hourglass_bottom</i>
                                Due:
                                {' '}
                                {formatDate(dueOn)}
                            </Box>
                            {
                                labId !== null
                                    ? (
                                        <Box className="info">
                                            <i className="material-icons" role="presentation">domain</i>
                                            {labName}
                                        </Box>
                                    )
                                    : (
                                        <Box className="info">
                                            <i className="material-icons" role="presentation">domain_disabled</i>
                                            No Lab
                                        </Box>
                                    )
                            }
                        </Stack>
                        <Box>
                            <div className="case-navigation">
                                <Button className="previous-case" onClick={prevCase} disabled={index <= 0} startIcon={<i className="material-icons">arrow_back</i>}>
                                    Previous
                                </Button>
                                <Button className="next-case" onClick={nextCase} disabled={index >= numCases - 1} endIcon={<i className="material-icons">arrow_forward</i>}>
                                    Next
                                </Button>
                            </div>
                        </Box>
                    </Box>
                    <Box className="lower-panel">
                        {
                            status === 'Processing'
                                ? (
                                    <Box className="left-panel dialog">
                                        <i className="material-icons status pending" role="presentation">info</i>
                                        Processing order information. Please try again later.
                                    </Box>
                                )
                                : (
                                    status === 'Processing Failed'
                                        ? (
                                            <Box className="left-panel dialog">
                                                <i className="material-icons status failed" role="presentation">error</i>
                                                Processing failed. Please re-upload and try again.
                                            </Box>
                                        )
                                        : (
                                            <Box className="left-panel-shell">
                                                <Box className="left-panel">
                                                    {
                                                        template === 'Clear Aligner' ? (
                                                            leftPanel === 0 ? (
                                                                <ClearAlignerThumbnails url={thumbnailUrl} />
                                                            ) : (leftPanel === 1 ? (
                                                                <Renders
                                                                    activeUrl={activeUrl}
                                                                    activeScan={activeScan}
                                                                    scans={orderedScans}
                                                                    viewMode={viewMode}
                                                                    activeViewIndex={activeViewIndex}
                                                                    setActiveViewIndex={setActiveViewIndex}
                                                                    showTips={showRenderTips}
                                                                    setShowTips={(nextValue) => setCaseDetailGlobalState((currentState) => ({
                                                                        ...currentState,
                                                                        showRenderTips: nextValue,
                                                                    }))}
                                                                    id={id}
                                                                />
                                                            ) : leftPanel === 2 && (
                                                                <Box className="pdf-container">
                                                                    <NonClearAlignerPdf prescriptionUrl={prescriptionUrl} />
                                                                </Box>
                                                            ))
                                                            // : leftPanel === 3 && (
                                                            //     'Photos Carousel Coming Soon'
                                                            // )
                                                        ) : (leftPanel === 0 ? (
                                                            <Renders
                                                                activeUrl={activeUrl}
                                                                activeScan={activeScan}
                                                                scans={orderedScans}
                                                                viewMode={viewMode}
                                                                activeViewIndex={activeViewIndex}
                                                                setActiveViewIndex={setActiveViewIndex}
                                                                showTips={showRenderTips}
                                                                setShowTips={(nextValue) => setCaseDetailGlobalState((currentState) => ({
                                                                    ...currentState,
                                                                    showRenderTips: nextValue,
                                                                }))}
                                                                id={id}
                                                            />
                                                        ) : leftPanel === 1 && (
                                                            <Box className="pdf-container">
                                                                <NonClearAlignerPdf prescriptionUrl={prescriptionUrl} />
                                                            </Box>
                                                        ))
                                                        // : leftPanel === 2 && (
                                                        //     'Photos Carousel Coming Soon'
                                                        // )
                                                    }
                                                </Box>
                                                <Box className="toggle-left-panel">
                                                    <Box className="view-menu">
                                                        {
                                                            template === 'Clear Aligner' ? (
                                                                <Stack className="buttons" spacing="5px" direction="row" divider={<Divider orientation="vertical" flexItem sx={{ my: '5px' }} />}>
                                                                    {/* Add jsx to include/exclude STL and PLY options if present or not */}
                                                                    <Button className={`button no-caps ${leftPanel === 0 ? 'selected' : ''}`} onClick={() => handleLeftPanelChange(0)}>
                                                                        Clear Aligner
                                                                    </Button>
                                                                    <Button className={`button no-caps ${leftPanel === 1 ? 'selected' : ''}`} onClick={() => handleLeftPanelChange(1)}>
                                                                        Scans
                                                                    </Button>
                                                                    <Button className={`button no-caps ${leftPanel === 2 ? 'selected' : ''}`} onClick={() => handleLeftPanelChange(2)}>
                                                                        Rx
                                                                    </Button>
                                                                    {/* <Button className={`button no-caps ${leftPanel === 3 ? 'selected' : ''}`} onClick={() => handleLeftPanelChange(3)}>
                                                                        Thumbnails
                                                                    </Button> */}
                                                                </Stack>
                                                            ) : (
                                                                <Stack className="buttons" spacing="5px" direction="row" divider={<Divider orientation="vertical" flexItem sx={{ my: '5px' }} />}>
                                                                    {/* Add jsx to include/exclude STL and PLY options if present or not */}
                                                                    <Button className={`button no-caps ${leftPanel === 0 ? 'selected' : ''}`} onClick={() => handleLeftPanelChange(0)}>
                                                                        Scans
                                                                    </Button>
                                                                    <Button className={`button no-caps ${leftPanel === 1 ? 'selected' : ''}`} onClick={() => handleLeftPanelChange(1)}>
                                                                        Rx
                                                                    </Button>
                                                                    {/* <Button className={`button no-caps ${leftPanel === 2 ? 'selected' : ''}`} onClick={() => handleLeftPanelChange(2)}>
                                                                        Thumbnails
                                                                    </Button> */}
                                                                </Stack>
                                                            )
                                                        }
                                                        {/* <Box
                                                            className="sliding-indicator"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: `${leftPanel === 0 ? '0' : leftPanel === 1 ? '55.89' : `${55.89 + 33.38}`}px`,
                                                                width: `${leftPanel === 0 ? '55.89' : leftPanel === 1 ? '33.38' : '95.1'}px`,
                                                                height: '100%',
                                                                border: `1px solid ${Ori.white}`,
                                                                borderRadius: `${Features.borderRadius}`,
                                                                bgcolor: 'transparent',
                                                                transition: '300ms ease',
                                                            }}
                                                        /> */}
                                                    </Box>
                                                    {
                                                        template === 'Clear Aligner' ? (
                                                            leftPanel === 1 && (
                                                                <Stack direction="column" className="scan-type transparent">
                                                                    <Stack className="type-menu">
                                                                        <Tabs
                                                                            value={viewMode}
                                                                            orientation="vertical"
                                                                            textColor="secondary"
                                                                            indicatorColor="secondary"
                                                                            variant="fullWidth"
                                                                            onChange={(event, value) => setViewMode(value)}
                                                                        >
                                                                            <Tab
                                                                                className={`type button round-border black pr ${viewMode === 'thumbnail' ? 'selected' : ''}`}
                                                                                label="Thumbnail"
                                                                                value="thumbnail"
                                                                                disabled={!activeScan?.thumbnailUrl}
                                                                            />
                                                                            <Tab
                                                                                className={`type button round-border vertical black pr ${viewMode === 'stl' ? 'selected' : ''}`}
                                                                                label="STL"
                                                                                value="stl"
                                                                                disabled={!activeScan?.stlUrl}
                                                                            />
                                                                            <Tab
                                                                                className={`type button round-border vertical black pr ${viewMode === 'ply' ? 'selected' : ''}`}
                                                                                label="PLY"
                                                                                value="ply"
                                                                                disabled={!activeScan?.plyUrl}
                                                                            />
                                                                        </Tabs>
                                                                    </Stack>
                                                                </Stack>
                                                            )
                                                        ) : (
                                                            leftPanel === 0 && (
                                                                <Stack direction="column" className="scan-type transparent">
                                                                    <Stack className="type-menu">
                                                                        <Tabs
                                                                            value={viewMode}
                                                                            orientation="vertical"
                                                                            textColor="secondary"
                                                                            indicatorColor="secondary"
                                                                            variant="fullWidth"
                                                                            onChange={(event, value) => setViewMode(value)}
                                                                        >
                                                                            <Tab
                                                                                className={`type button round-border black pr ${viewMode === 'thumbnail' ? 'selected' : ''}`}
                                                                                label="Thumbnail"
                                                                                value="thumbnail"
                                                                                disabled={!activeScan?.thumbnailUrl}
                                                                            />
                                                                            <Tab
                                                                                className={`type button round-border vertical black pr ${viewMode === 'stl' ? 'selected' : ''}`}
                                                                                label="STL"
                                                                                value="stl"
                                                                                disabled={!activeScan?.stlUrl}
                                                                            />
                                                                            <Tab
                                                                                className={`type button round-border vertical black pr ${viewMode === 'ply' ? 'selected' : ''}`}
                                                                                label="PLY"
                                                                                value="ply"
                                                                                disabled={!activeScan?.plyUrl}
                                                                            />
                                                                        </Tabs>
                                                                    </Stack>
                                                                </Stack>
                                                            )
                                                        )
                                                    }
                                                </Box>
                                            </Box>
                                        )
                                )
                        }
                        {/* Actions / Case Details */}
                        <Box className={`right-panel-shell ${isRightPanelOpen ? '' : 'collapsed'}`}>
                            <button
                                type="button"
                                className="right-panel-toggle"
                                onClick={() => setCaseDetailGlobalState((currentState) => ({
                                    ...currentState,
                                    isRightPanelOpen: !currentState.isRightPanelOpen,
                                }))}
                                aria-label={isRightPanelOpen ? 'Collapse case side panel' : 'Expand case side panel'}
                                title={isRightPanelOpen ? 'Hide panel' : 'Show panel'}
                            >
                                <i className="material-icons" aria-hidden="true">
                                    {isRightPanelOpen ? 'chevron_right' : 'chevron_left'}
                                </i>
                            </button>
                            <Box className="right-panel" sx={isRightPanelOpen ? { borderLeft: 1, borderColor: 'divider' } : undefined}>
                                <Box className="tab-buttons">
                                    {
                                        (status === 'Processing' || status === 'Processing Failed')
                                            ? (
                                                <Tabs value={selectedRightPanel} textColor="primary" indicatorColor="primary" variant="fullWidth" onChange={handleRightPanelChange}>
                                                    <Tab label="Actions" value={0} sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '15px' }} disabled />
                                                    <Tab label="Details" value={1} sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '15px' }} />
                                                </Tabs>
                                            )
                                            : (
                                                <Tabs value={selectedRightPanel} textColor="primary" indicatorColor="primary" variant="fullWidth" onChange={handleRightPanelChange}>
                                                    <Tab label="Actions" value={0} sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '15px' }} />
                                                    <Tab label="Details" value={1} sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '15px' }} />
                                                </Tabs>
                                            )
                                    }
                                </Box>
                                {
                                    selectedRightPanel === 0
                                        ? (
                                            <Box className="actions-case-details-container alternate">
                                                <Download
                                                    id={id}
                                                    id8={id8}
                                                    orderIntegrationUploadZipUrl={orderIntegrationUploadZipUrl}
                                                    prescriptionUrl={prescriptionUrl}
                                                    scanZipUrl={scanZipUrl}
                                                    canDownloadOrderIntegrationUploadZip={canDownloadOrderIntegrationUploadZip}
                                                    canDownloadPrescription={canDownloadPrescription}
                                                    canUpdateCaseStatus={canUpdateCaseStatus}
                                                    canDownloadSTL={canDownloadSTL}
                                                    onCaseStatusChange={onCaseStatusChange}
                                                />
                                                { status === 'Submitted' && hasAccess('cases:write') && (
                                                    <Button className="add-lab-button more-padding" onClick={onOpenAddLabModal} sx={{ border: 1, borderColor: 'divider' }}>
                                                        {
                                                            labId != null
                                                                ? (
                                                                    <>
                                                                        <i className="material-icons">domain</i>
                                                                        Change Lab
                                                                    </>
                                                                )
                                                                : (
                                                                    <>
                                                                        <i className="material-icons">domain_add</i>
                                                                        Add Lab
                                                                    </>
                                                                )
                                                        }
                                                    </Button>
                                                )}
                                            </Box>
                                        )
                                        : (
                                            <Box className="actions-case-details-container default">
                                                <Grid container columns={2} className="detail">
                                                    <Grid item xs={1} className="info">
                                                        Patient:
                                                    </Grid>
                                                    <Grid item xs={1} className="data">
                                                        {patientName != null ? patientName : '-'}
                                                    </Grid>
                                                </Grid>
                                                <Grid container columns={2} className="detail">
                                                    <Grid item xs={1} className="info">
                                                        Doctor:
                                                    </Grid>
                                                    <Grid item xs={1} className="data">
                                                        {doctorName != null ? doctorName : '-'}
                                                    </Grid>
                                                </Grid>
                                                <Divider orientation="horizontal" flexItem sx={{ my: '5px' }} />
                                                <Grid container columns={2} className="detail">
                                                    <Grid item xs={1} className="info">
                                                        Case ID:
                                                    </Grid>
                                                    <Grid item xs={1} className="data" onClick={() => { onCopy('short'); }}>
                                                        <div className="action copy">
                                                            <span className="copy-text" title={id8 != null ? id8 : '-'}>
                                                                {id8 != null ? id8 : '-'}
                                                            </span>
                                                            <i className="material-icons copy-icon" role="presentation">content_copy</i>
                                                            {
                                                                showFullID === false && (
                                                                    <AnimateOnMount
                                                                        className="overlay-wrapper"
                                                                        duration={200}
                                                                        from={{ opacity: 0 }}
                                                                        to={{ opacity: 1 }}
                                                                    >
                                                                        <div className={`overlay ${showOverlay ? 'visible' : ''} border`}>
                                                                            Copied!
                                                                        </div>
                                                                    </AnimateOnMount>
                                                                )
                                                            }
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid container columns={2} className="detail">
                                                    <Grid item xs={1} className="info">
                                                        Full Case ID:
                                                    </Grid>
                                                    <Grid item xs={1} className="data" onClick={() => { onCopy('full'); }}>
                                                        <div className="action copy full-id-copy">
                                                            <span className="copy-text full-id" title={id != null ? id : '-'}>
                                                                {displayFullId}
                                                            </span>
                                                            <i className="material-icons copy-icon" role="presentation">content_copy</i>
                                                            {
                                                                showFullID === true && (
                                                                    <AnimateOnMount
                                                                        className="overlay-wrapper"
                                                                        duration={200}
                                                                        from={{ opacity: 0 }}
                                                                        to={{ opacity: 1 }}
                                                                    >
                                                                        <div className={`overlay ${showOverlay ? 'visible' : ''} border`}>
                                                                            Copied!
                                                                        </div>
                                                                    </AnimateOnMount>
                                                                )
                                                            }
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                <Grid container columns={2} className="detail">
                                                    <Grid item xs={1} className="info">
                                                        Clinic:
                                                    </Grid>
                                                    <Grid item xs={1} className="data">
                                                        {clinicName != null ? clinicName : '-'}
                                                    </Grid>
                                                </Grid>
                                                <Grid container columns={2} className="detail">
                                                    <Grid item xs={1} className="info">
                                                        Lab:
                                                    </Grid>
                                                    <Grid item xs={1} className="data">
                                                        {labName != null ? labName : '-'}
                                                    </Grid>
                                                </Grid>
                                                <Divider orientation="horizontal" flexItem sx={{ my: '5px' }} />
                                                <Grid container columns={2} className="detail">
                                                    <Grid item xs={1} className="info">
                                                        Created:
                                                    </Grid>
                                                    <Grid item xs={1} className="data">
                                                        {formatDate(createdOn)}
                                                    </Grid>
                                                </Grid>
                                                <Grid container columns={2} className="detail">
                                                    <Grid item xs={1} className="info">
                                                        Recieved:
                                                    </Grid>
                                                    <Grid item xs={1} className="data">
                                                        {formatDate(receivedOn)}
                                                    </Grid>
                                                </Grid>
                                                <Grid container columns={2} className="detail">
                                                    <Grid item xs={1} className="info">
                                                        Due:
                                                    </Grid>
                                                    <Grid item xs={1} className="data">
                                                        {formatDate(dueOn)}
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )
                                }
                            </Box>
                        </Box>
                    </Box>
                    <Dialog onClose={onCloseAddLabModal} open={showAddLabModal}>
                        <div className="modal-lab-picker">
                            <LabPicker
                                labs={labs}
                                selectedLab={chosenLab?.id || labId}
                                onSelectLab={setChosenLab}
                                includeNone
                                returnObject
                            />
                            <div className="dialog-button-container">
                                <button type="button" className="btn btn-icon full-width" onClick={onCloseAddLabModal}>
                                    <i className="material-icons">cancel</i>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-icon full-width" onClick={onMoveCaseToLab}>
                                    <i className="material-icons">save</i>
                                    {labId && chosenLab?.id === 'none' ? 'Unassign' : 'Assign'}
                                </button>
                            </div>
                        </div>
                    </Dialog>
                </Box>
            </Modal>
        </ThemeProvider>
    );
}

CaseDetail.propTypes = {
    id: PropTypes.string.isRequired,
    orderIntegrationUploadZipUrl: PropTypes.string,
    prescriptionUrl: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    scanZipUrl: PropTypes.string,
    template: PropTypes.string,
    // instructions: PropTypes.string,
    patientName: PropTypes.string,
    doctorName: PropTypes.string,
    labId: PropTypes.string,
    labName: PropTypes.string,
    clinicName: PropTypes.string,
    status: PropTypes.string,
    createdOn: PropTypes.string,
    receivedOn: PropTypes.string,
    dueOn: PropTypes.string,
    scans: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        dentalCaseId: PropTypes.string,
        label: PropTypes.string,
        displayLabel: PropTypes.string,
        displayIndex: PropTypes.number,
        stlUrl: PropTypes.string,
        plyUrl: PropTypes.string,
    })).isRequired,
    numCases: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
    labs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
    })),
    setOpen: PropTypes.func.isRequired,
    caseDetailGlobalState: PropTypes.shape({
        isRightPanelOpen: PropTypes.bool.isRequired,
        rightPanel: PropTypes.number.isRequired,
        showRenderTips: PropTypes.bool.isRequired,
    }).isRequired,
    setCaseDetailGlobalState: PropTypes.func.isRequired,
    onCaseStatusChange: PropTypes.func,
    onCaseLabChange: PropTypes.func,
};

CaseDetail.defaultProps = {
    orderIntegrationUploadZipUrl: '',
    prescriptionUrl: '',
    thumbnailUrl: '',
    scanZipUrl: '',
    template: '',
    // instructions: '',
    patientName: null,
    doctorName: null,
    labId: null,
    labName: null,
    createdOn: null,
    receivedOn: null,
    dueOn: null,
    clinicName: null,
    status: '',
    labs: [],
    onCaseStatusChange: () => {},
    onCaseLabChange: () => {},
};

export default CaseDetail;
